import { createSlice, createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import { selectTableById } from './tablesSlice';

const selectOrderId = (order) => order._id || order.id;
const ordersAdapter = createEntityAdapter({
	selectId: selectOrderId,
	sortComparer: (a, b) => new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at),
});

const initialState = ordersAdapter.getInitialState({
	statusFilter: 'active',
	// Bumped on every AUTHORITATIVE reconcile (full fetchActiveOrders setAll
	// and sync setOrders). Local hydration records the epoch before its slow
	// storage reads and DROPS its result if the epoch advanced meanwhile — a
	// late local read must never overwrite fresher server truth (ghost-order
	// resurrection race caught in the Phase A adversarial review).
	reconcileEpoch: 0,
	loading: false,
	creating: false,
	updating: false,
	error: null,
	kdsFilter: 'all', // For KDS: 'all', 'preparing', 'ready', 'served'
	modification: {
		activeOrderId: null,
		originalStatus: null,
		originalItems: [],   // deep-copy of items at the moment modification started (for offline diff)
	},
	// History state
	history: {
		orders: [],
		loading: false,
		error: null,
		filters: {
			statusFilter: 'all',
			searchTerm: '',
			startDate: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T00:00`; })(),
			endDate: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T23:59`; })(),
		},
		pagination: {
			currentPage: 1,
			totalPages: 0,
			total: 0,
			limit: 50,
		},
	},
});

// Ghost guard shared by the fulfilled-matchers below (same rule as the
// upsertOrder reducer): a payload without an items array may only UPDATE an
// order we already know — it may never CREATE one. Thunks fulfil with stubs
// like { _id, id, status, updatedAt } when offline fallbacks fire; if the id
// was remapped mid-flight, upsertOne would mint a bare entity that crashes
// `order.items.map` and seeds skeleton rows into the sync queue.
const guardedUpsertOne = (state, row) => {
	const rowId = String(row?._id || row?.id || '');
	if (!rowId) return;
	if (state.entities[rowId] === undefined && !Array.isArray(row?.items)) {
		console.warn('[orders] ignored partial fulfilled-upsert for an unknown order id', {
			rowId, keys: Object.keys(row || {}).slice(0, 10),
		});
		return;
	}
	ordersAdapter.upsertOne(state, row);
};

const ordersSlice = createSlice({
	name: 'orders',
	initialState,
	reducers: {
		setOrders: (state, action) => {
			ordersAdapter.setAll(state, action.payload || []);
			state.reconcileEpoch += 1;
			state.loading = false;
			state.error = null;
		},
		// Bulk ADDITIVE apply — local-first read paths use this instead of
		// setOrders/setAll: it can only add or refresh rows, never remove, so
		// a partial source (local hydration, a per-table server fetch) can
		// never make orders vanish from the screen. Removals happen only via
		// explicit actions or a full authoritative reconcile.
		upsertOrders: (state, action) => {
			const rows = Array.isArray(action.payload) ? action.payload : [];
			if (!rows.length) return;
			// FRESHNESS GUARD. This additive bulk apply is the popup-reopen refresh
			// path (getOrdersAndTableInfo → upsertOrders) and other server refreshes.
			// It must NOT overwrite an entity holding an un-acked local edit
			// (_isLocal) with a stale server row — that rolled a just-modified order
			// back to its pre-modification server state until the pump replayed
			// ("reopen shows the old cart, takes time to update"). Local is the
			// source of truth until the server acknowledges it; clearLocalFlags drops
			// _isLocal on settle, after which server rows apply normally. A row that
			// is ITSELF _isLocal (a preserved local entity being re-added) always
			// applies.
			const safe = rows.filter((row) => {
				const id = String(row?._id || row?.id || '');
				const existing = id ? state.entities[id] : null;
				if (!existing || existing._isLocal !== true) return true; // nothing pending to protect
				if (row?._isLocal === true) return true;                  // re-adding a local entity
				// Escape hatch: a server-confirmed TERMINAL status is authoritative
				// (another device completed/cancelled this order) and must never be
				// shadowed by a pending local edit — otherwise a poison-pill row that
				// never settles could hide a real completion until app restart.
				if (row?.status === 'completed' || row?.status === 'cancelled') return true;
				return false;                                             // drop stale ACTIVE server row over pending local edit
			});
			if (safe.length) ordersAdapter.upsertMany(state, safe);
		},
		upsertOrder: (state, action) => {
			const row = action.payload;
			const rowId = String(row?._id || row?.id || '');
			if (!rowId) return;
			// GHOST GUARD. A field-stamp aimed at an id that no longer exists
			// (an order remapped local_→server mid-flight) would CREATE a bare
			// entity carrying only those few keys. That ghost crashed the
			// checkout popup on `order.items.map` and seeded skeleton rows into
			// the sync queue that blanked carts server-side (2026-08-13).
			// A payload with no items may only UPDATE an order we already know;
			// it may never bring a new one into existence.
			const isNewEntity = state.entities[rowId] === undefined;
			if (isNewEntity && !Array.isArray(row?.items)) {
				console.warn('[orders] ignored partial upsert for an unknown order id', {
					rowId, keys: Object.keys(row || {}).slice(0, 10),
				});
				return;
			}
			ordersAdapter.upsertOne(state, row);
		},
		// Phase B: after the sync engine settles a queue row, the entity's
		// _isLocal flag must drop too — a permanently-true flag made the modal
		// reconcile's local-preservation shadow fresh server truth forever
		// (e.g. re-showing an order another device completed). Payload: array
		// of settled row keys; matched against entity id OR localId.
		clearLocalFlags: (state, action) => {
			const keys = new Set((action.payload || []).map(String));
			if (keys.size === 0) return;
			for (const id of state.ids) {
				const o = state.entities[id];
				if (!o || o._isLocal !== true) continue;
				if (keys.has(String(o._id || o.id || '')) || keys.has(String(o.localId || ''))) {
					o._isLocal = false;
				}
			}
		},
		updateOrderStatus: (state, action) => {
			const { orderId, status, ...updates } = action.payload;
			const order = state.entities[orderId];
			if (order) {
				ordersAdapter.updateOne(state, {
					id: orderId,
					changes: {
						status,
						...updates,
					},
				});
			}
		},
		removeOrderById: (state, action) => {
			ordersAdapter.removeOne(state, action.payload);
		},
		clearOrders: (state) => {
			ordersAdapter.removeAll(state);
		},
		setStatusFilter: (state, action) => {
			state.statusFilter = action.payload;
		},
		setKDSFilter: (state, action) => {
			state.kdsFilter = action.payload;
		},
		setLoading: (state, action) => {
			state.loading = action.payload;
		},
		setCreating: (state, action) => {
			state.creating = action.payload;
		},
		setUpdating: (state, action) => {
			state.updating = action.payload;
		},
		setError: (state, action) => {
			state.error = action.payload;
		},
		// Modification lifecycle reducers
		startModification: (state, action) => {
			const { orderId, originalStatus } = action.payload;
			// Resolve through the localId fallback: a direct entities[orderId]
			// lookup misses when the pump remapped local_→server id moments ago,
			// and snapshotting originalItems = [] then corrupts the whole cycle —
			// the finish diff claims every item was "Added" and prints a wrong
			// kitchen slip (audit: critical, modify-cycle).
			let order = state.entities[orderId];
			let resolvedId = orderId;
			if (!order) {
				for (const id of state.ids) {
					const o = state.entities[id];
					if (o?.localId && String(o.localId) === String(orderId)) {
						order = o;
						resolvedId = o._id || o.id || id;
						break;
					}
				}
			}
			state.modification.activeOrderId = resolvedId;
			state.modification.originalStatus = originalStatus;
			// Snapshot the current items so the offline finishOrderModification thunk
			// can compute the exact diff (added / removed / quantity-changed).
			state.modification.originalItems = order?.items
				? JSON.parse(JSON.stringify(order.items))
				: [];
		},
		finishModification: (state, action) => {
			state.modification.activeOrderId = null;
			state.modification.originalStatus = null;
			state.modification.originalItems  = [];
		},
		cancelModification: (state) => {
			state.modification.activeOrderId = null;
			state.modification.originalStatus = null;
			state.modification.originalItems  = [];
		},
		// Item manipulation reducers (optimistic updates)
		addItemToOrder: (state, action) => {
			const { orderId, item, modificationCycle } = action.payload;
			const order = state.entities[orderId];
			
			
			if (order) {
				const newItem = {
					...item,
					addedAt: item.addedAt || new Date().toISOString(),
					modificationCycle: item.modificationCycle !== undefined 
						? item.modificationCycle 
						: (modificationCycle !== undefined ? modificationCycle : (order.currentModificationCycle || 0)),
				};
				order.items = [...(order.items || []), newItem];
				
			} else {
			}
		},
		removeItemFromOrder: (state, action) => {
			const { orderId, itemId } = action.payload;
			const order = state.entities[orderId];
			if (order && order.items) {
				order.items = order.items.filter(item => 
					String(item.cartItemId || item._id || item.id || item.menuItemId) !== String(itemId)
				);
			}
		},
		updateItemQuantity: (state, action) => {
			const { orderId, itemId, quantity } = action.payload;
			const order = state.entities[orderId];
			if (order && order.items) {
				const item = order.items.find(item =>
					String(item.cartItemId || item._id || item.id || item.menuItemId) === String(itemId)
				);
				if (item) {
					item.quantity = quantity;
					item.amount = (item.price || 0) * quantity;
				}
			}
		},
		// Update the selectedOptions (custom modifiers) for a single cart item.
		// selectedOptions: [{ groupId, title, choice }]
		updateItemPrice: (state, action) => {
			const { orderId, itemId, price } = action.payload;
			const order = state.entities[orderId];
			if (order && order.items) {
				const item = order.items.find(i =>
					String(i.cartItemId || i._id || i.id || i.menuItemId) === String(itemId)
				);
				if (item) {
					item.price = price;
					item.amount = price * (item.quantity || 1);
				}
			}
		},
		updateItemOptions: (state, action) => {
			const { orderId, itemId, selectedOptions, note } = action.payload;
			const order = state.entities[orderId];
			if (order && order.items) {
				const item = order.items.find(i =>
					String(i.cartItemId || i._id || i.id || i.menuItemId) === String(itemId)
				);
				if (item) {
					item.selectedOptions = selectedOptions;
					item.notes = typeof note === 'string' ? note.trim() : '';
					item.isBucketLocked = true;
				}
			}
		},
		// Toggle the "no print" flag on a single cart item. The item stays on the
		// order and the bill, but the kitchen/modification print builders skip it.
		toggleItemNoPrint: (state, action) => {
			const { orderId, itemId } = action.payload;
			const order = state.entities[orderId];
			if (order && order.items) {
				const item = order.items.find(i =>
					String(i.cartItemId || i._id || i.id || i.menuItemId) === String(itemId)
				);
				if (item) {
					item.noPrint = !item.noPrint;
				}
			}
		},
		// Clear the "no print" flag from every item on an order (reset after print).
		clearOrderNoPrint: (state, action) => {
			const { orderId } = action.payload;
			const order = state.entities[orderId];
			if (order && order.items) {
				order.items.forEach((item) => { if (item.noPrint) delete item.noPrint; });
			}
		},
		markServed: (state, action) => {
			const { orderId, timestamp } = action.payload;
			const order = state.entities[orderId];
			if (order) {
				order.lastServedAt = timestamp;
				order.totalServeCount = (order.totalServeCount || 0) + 1;
			}
		},
		// Create draft order in frontend state (not saved to backend yet)
		createDraftOrder: (state, action) => {
			// Tolerate double-wrapping: the hook wraps its argument in {orderData},
			// and call sites wrapped AGAIN — the reducer then saw no _id, minted a
			// fresh draft id, and stored a tableId-less junk twin that no cleanup
			// could match (one immortal twin per placed order; 31 accumulated in a
			// day at Ayder, 15 at Biryani). Unwrap however deep the payload came.
			let { orderData } = action.payload;
			if (orderData && orderData.orderData && !orderData._id && !orderData.tableId) {
				orderData = orderData.orderData;
			}
			// Use provided ID if available, otherwise generate one
			const draftId = orderData._id || orderData.id || `draft_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
			
			const draftOrder = {
				...orderData,
				_id: draftId,
				id: draftId,
				status: orderData.status || 'draft',
				items: orderData.items || [],
				subtotal: orderData.subtotal || 0,
				tax: orderData.tax || 0,
				total: orderData.total || 0,
				currentModificationCycle: orderData.currentModificationCycle || 0,
				totalServeCount: orderData.totalServeCount || 0,
				createdAt: orderData.createdAt || new Date().toISOString(),
				updatedAt: orderData.updatedAt || new Date().toISOString(),
				isDraft: true, // Flag to identify draft orders
			};
			
			ordersAdapter.upsertOne(state, draftOrder);
		},
		// History reducers
		setHistoryFilters: (state, action) => {
			state.history.filters = {
				...state.history.filters,
				...action.payload,
			};
		},
		setHistoryPage: (state, action) => {
			state.history.pagination.currentPage = action.payload;
		},
		clearHistoryFilters: (state) => {
			const d = new Date();
			const t = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
			state.history.filters = {
				statusFilter: 'all',
				searchTerm: '',
				startDate: `${t}T00:00`,
				endDate: `${t}T23:59`,
			};
			state.history.pagination.currentPage = 1;
		},
		// Fix 6 — remap local_ IDs to real server UUIDs after sync
		// Fix E — also patch orderNumber when server returns it
		// Supports both legacy string format { "local_x": "uuid" }
		// and new object format { "local_x": { id: "uuid", orderNumber: 1234 } }
		remapOrderIds: (state, action) => {
			Object.entries(action.payload || {}).forEach(([localId, mapping]) => {
				const serverId    = typeof mapping === 'string' ? mapping : mapping?.id;
				const orderNumber = typeof mapping === 'object'  ? mapping?.orderNumber : undefined;
				const order = state.entities[localId];
				if (order && serverId) {
					ordersAdapter.removeOne(state, localId);
					ordersAdapter.upsertOne(state, {
						...order,
						_id: serverId,
						id:  serverId,
						...(orderNumber != null ? { orderNumber } : {}),
					});
				}
				// A modification session in flight against the old local id must
				// follow the order to its server id, or finish/cancel later
				// resolves nothing ("Order not found") and the diff is computed
				// against the wrong target (audit: family B).
				if (serverId && String(state.modification.activeOrderId) === String(localId)) {
					state.modification.activeOrderId = serverId;
				}
			});
		},
	},
	extraReducers: (builder) => {
		// Handle async thunks from orderThunks.js
		builder
			// Create order
			.addMatcher(
				(action) => action.type?.startsWith('orders/createOrder'),
				(state, action) => {
					if (action.type.endsWith('/pending')) {
						state.creating = true;
						state.error = null;
					} else if (action.type.endsWith('/fulfilled')) {
						state.creating = false;
						if (action.payload) {
							ordersAdapter.upsertOne(state, action.payload);
						}
					} else if (action.type.endsWith('/rejected')) {
						state.creating = false;
						state.error = action.error?.message || 'Failed to create order';
					}
				}
			)
			// Update order status
			.addMatcher(
				(action) => action.type?.startsWith('orders/updateOrderStatus'),
				(state, action) => {
					if (action.type.endsWith('/pending')) {
						state.updating = true;
						state.error = null;
					} else if (action.type.endsWith('/fulfilled')) {
						state.updating = false;
						if (action.payload) {
							guardedUpsertOne(state, action.payload);
							// Also patch history.orders so getOrderDisplayStatus sees fresh billedAt / modificationHistory
							const updatedId = action.payload._id || action.payload.id;
							const histIdx = state.history.orders.findIndex(
								(o) => (o._id || o.id) === updatedId
							);
							if (histIdx !== -1) {
								state.history.orders[histIdx] = {
									...state.history.orders[histIdx],
									...action.payload,
								};
							}
						}
					} else if (action.type.endsWith('/rejected')) {
						state.updating = false;
						state.error = action.error?.message || 'Failed to update order status';
					}
				}
			)
			// Update order (full update)
			.addMatcher(
				(action) => action.type?.startsWith('orders/updateOrder'),
				(state, action) => {
					if (action.type.endsWith('/pending')) {
						state.updating = true;
						state.error = null;
					} else if (action.type.endsWith('/fulfilled')) {
						state.updating = false;
						if (action.payload) {
							guardedUpsertOne(state, action.payload);
							// Also patch history.orders so getOrderDisplayStatus sees fresh billedAt / modificationHistory
							const updatedId = action.payload._id || action.payload.id;
							const histIdx = state.history.orders.findIndex(
								(o) => (o._id || o.id) === updatedId
							);
							if (histIdx !== -1) {
								state.history.orders[histIdx] = {
									...state.history.orders[histIdx],
									...action.payload,
								};
							}
						}
					} else if (action.type.endsWith('/rejected')) {
						state.updating = false;
						state.error = action.error?.message || 'Failed to update order';
					}
				}
			)
			// Mark order (KDS operations)
			.addMatcher(
				(action) => action.type?.startsWith('orders/markOrder'),
				(state, action) => {
					if (action.type.endsWith('/pending')) {
						state.updating = true;
						state.error = null;
					} else if (action.type.endsWith('/fulfilled')) {
						state.updating = false;
						if (action.payload) {
							guardedUpsertOne(state, action.payload);
						}
					} else if (action.type.endsWith('/rejected')) {
						state.updating = false;
						state.error = action.error?.message || 'Failed to mark order';
					}
				}
			)
			// Fetch orders
			.addMatcher(
				(action) => action.type?.startsWith('orders/fetchOrders'),
				(state, action) => {
					if (action.type.endsWith('/pending')) {
						state.loading = true;
						state.error = null;
					} else 					if (action.type.endsWith('/fulfilled')) {
						state.loading = false;
						// Merge-protection: a plain setAll wipes orders that exist only on
						// this device — drafts being composed and offline-created orders
						// awaiting sync. The server can't know about those yet, so a
						// background refresh must never delete them (this was the "order
						// vanished until app restart" bug). Preserve localish orders unless
						// the payload shows they've synced (a server row carries our
						// local id in local_id).
						const serverOrders = action.payload || [];
						const preservedLocal = Object.values(state.entities || {}).filter((o) => {
							// Server-id entities flagged _isLocal carry an edit the pump
							// hasn't settled yet — local is fresher by definition (settle
							// clears the flag once the server acknowledges). Without this,
							// a background refresh rolled the edit back on screen until
							// the next pump round (same rule as the sync-modal apply).
							if (o?._isLocal === true) return true;
							const oid = String(o?._id || o?.id || '');
							const isLocalish = oid.startsWith('draft_') || oid.startsWith('local_');
							if (!isLocalish) return false;
							return !serverOrders.some(
								(s) => String(s?.local_id || s?.localId || '') === oid
							);
						});
						ordersAdapter.setAll(state, serverOrders);
						state.reconcileEpoch += 1;
						if (preservedLocal.length > 0) {
							ordersAdapter.upsertMany(state, preservedLocal);
							// Each hit here is one order the old code would have wiped from
							// the screen ("vanished order") — track how often this fires.
							console.warn('[orders] preserved local orders from refresh wipe', {
								count: preservedLocal.length,
								ids: preservedLocal.map((o) => String(o?._id || o?.id || '')),
							});
						}
					} else if (action.type.endsWith('/rejected')) {
						state.loading = false;
						state.error = action.error?.message || 'Failed to fetch orders';
					}
				}
			)
			// Fetch order history
			.addMatcher(
				(action) => action.type?.startsWith('orders/fetchOrderHistory'),
				(state, action) => {
					if (action.type.endsWith('/pending')) {
						state.history.loading = true;
						state.history.error = null;
					} else if (action.type.endsWith('/fulfilled')) {
						state.history.loading = false;
						if (action.payload) {
							state.history.orders = action.payload.orders || [];
							state.history.pagination = {
								...state.history.pagination,
								...action.payload.pagination,
							};
							// Also upsert orders into main orders entity adapter for consistency
							if (action.payload.orders && action.payload.orders.length > 0) {
								ordersAdapter.upsertMany(state, action.payload.orders);
							}
						}
					} else if (action.type.endsWith('/rejected')) {
						state.history.loading = false;
						state.history.error = action.error?.message || 'Failed to fetch order history';
					}
				}
			);
		
		// Delete order (soft delete)
		builder.addMatcher(
			(action) => action.type?.startsWith('orders/deleteOrder'),
			(state, action) => {
				if (action.type.endsWith('/pending')) {
					state.updating = true;
					state.error = null;
				} else if (action.type.endsWith('/fulfilled')) {
					state.updating = false;
					if (action.payload) {
						ordersAdapter.removeOne(state, action.payload);
					}
				} else if (action.type.endsWith('/rejected')) {
					state.updating = false;
					state.error = action.error?.message || 'Failed to delete order';
				}
			}
		);
	},
});

export const {
	setOrders,
	upsertOrders,
	upsertOrder,
	updateOrderStatus,
	removeOrderById,
	clearOrders,
	setStatusFilter,
	setKDSFilter,
	setLoading,
	setCreating,
	setUpdating,
	setError,
	startModification,
	finishModification,
	cancelModification,
	addItemToOrder,
	removeItemFromOrder,
	updateItemQuantity,
	updateItemPrice,
	updateItemOptions,
	toggleItemNoPrint,
	clearOrderNoPrint,
	markServed,
	createDraftOrder,
	setHistoryFilters,
	setHistoryPage,
	clearHistoryFilters,
	remapOrderIds,
	clearLocalFlags,
} = ordersSlice.actions;

export default ordersSlice.reducer;

// Selectors
const selectOrdersState = (state) => state.orders;

export const {
	selectAll: selectAllOrders,
	selectById: selectOrderById,
	selectIds: selectOrderIds,
} = ordersAdapter.getSelectors(selectOrdersState);

export const selectActiveOrders = createSelector(
	[selectAllOrders, (state) => state.orders.statusFilter],
	(orders, statusFilter) => {
		if (!statusFilter || statusFilter === 'all') return orders;
		return orders.filter((o) => (o.status || 'active') === statusFilter);
	}
);

export const selectKDSOrders = createSelector(
	[selectAllOrders, (state) => state.orders.kdsFilter],
	(orders, kdsFilter) => {
		// Filter orders that are in kitchen workflow.
		// Exclude locally-buffered orders (local_ / draft_ prefix) — they don't exist
		// on the server yet so KDS cannot receive or act on them.
		const kitchenOrders = orders.filter(
			(order) => {
				const id = order._id || order.id || '';
				if (typeof id === 'string' && (id.startsWith('local_') || id.startsWith('draft_'))) return false;
				return ['confirmed', 'preparing', 'ready', 'served'].includes(order.status) && !order.deletedAt;
			}
		);
		
		if (!kdsFilter || kdsFilter === 'all') return kitchenOrders;
		return kitchenOrders.filter((order) => order.status === kdsFilter);
	}
);

/**
 * Factory for a per-instance memoised selector.
 *
 * USAGE — call once at hook/component initialisation, NOT inline in useSelector:
 *   const selectOrders = useMemo(makeSelectOrdersByTableId, []);
 *   const orders = useSelector(state => selectOrders(state, tableId));
 *
 * Accepts tableId as the second selector argument so that a single selector
 * instance caches the last (state, tableId) pair — no new array reference
 * is returned unless the filtered list actually changed.
 */
export const makeSelectOrdersByTableId = () =>
	createSelector(
		[selectAllOrders, (_state, tableId) => tableId],
		(orders, tableId) =>
			orders.filter((o) => String(o.tableId || o.table_id) === String(tableId))
	);

/**
 * Selector to determine if a table is occupied
 * A table is considered occupied if:
 * 1. Table status is 'occupied'
 * 2. There are orders for the table
 * 3. None of the orders are in 'draft' or 'preparing' status (meaning they're confirmed/completed)
 */
export const makeSelectIsTableOccupied = (tableId, isVirtual = false, selectedOrderId = null) =>
	createSelector(
		[selectAllOrders, (state) => {
			// Safely get table from Redux state
			if (!state || !state.tables) return null;
			return selectTableById(state, tableId);
		}],
		(orders, table) => {
			if (!table) return false;
			
			// Check table status
			if (table.status !== 'occupied') return false;
			
			// Get orders for this table
			let tableOrders;
			if (isVirtual && selectedOrderId) {
				// For virtual tables, check specific order
				tableOrders = orders.filter(order => order._id === selectedOrderId || order.id === selectedOrderId);
			} else {
				// For physical tables, filter by tableId
				tableOrders = orders.filter(order => String(order.tableId || order.table_id) === String(tableId));
			}
			
			if (!tableOrders || tableOrders.length === 0) return false;
			
			// Table is occupied if there are no draft/preparing orders (all orders are confirmed/completed)
			const hasNewOrder = tableOrders.some(order => order.status === 'draft' || order.status === 'preparing');
			
			return !hasNewOrder;
		}
	);

export const selectOrdersLoading = (state) => state.orders.loading;
export const selectOrdersCreating = (state) => state.orders.creating;
export const selectOrdersUpdating = (state) => state.orders.updating;
export const selectOrdersError = (state) => state.orders.error;

// Modification selectors
export const selectModifyingOrder = createSelector(
	[selectAllOrders, (state) => state.orders.modification.activeOrderId],
	(orders, activeOrderId) => {
		if (!activeOrderId) return null;
		return orders.find(order => (order._id || order.id) === activeOrderId) || null;
	}
);

export const selectModificationState = (state) => state.orders.modification;

// KDS item filtering selector - filters items by lastServedAt
export const selectKDSItems = createSelector(
	[selectOrderById],
	(order) => {
		if (!order) return [];
		
		// If no serve checkpoint, show all items
		if (!order.lastServedAt) {
			return order.items || [];
		}
		
		// Show only items added after last serve
		const lastServedTime = new Date(order.lastServedAt).getTime();
		return (order.items || []).filter(item => {
			const itemAddedTime = new Date(item.addedAt || order.createdAt || order.created_at || Date.now()).getTime();
			return itemAddedTime > lastServedTime;
		});
	}
);

// Helper selector to get KDS items for a specific order ID
export const makeSelectKDSItems = (orderId) =>
	createSelector(
		[(state) => selectOrderById(state, orderId)],
		(order) => {
			if (!order) return [];
			
			// If no serve checkpoint, show all items
			if (!order.lastServedAt) {
				return order.items || [];
			}
			
			// Show only items added after last serve
			const lastServedTime = new Date(order.lastServedAt).getTime();
			return (order.items || []).filter(item => {
				const itemAddedTime = new Date(item.addedAt || order.createdAt || order.created_at || Date.now()).getTime();
				return itemAddedTime > lastServedTime;
			});
		}
	);

// History selectors
export const selectHistoryOrders = (state) => state.orders.history.orders;
export const selectHistoryLoading = (state) => state.orders.history.loading;
export const selectHistoryError = (state) => state.orders.history.error;
export const selectHistoryFilters = (state) => state.orders.history.filters;
export const selectHistoryPagination = (state) => state.orders.history.pagination;

/** Resolves API camelCase or DB snake_case on modification records. */
export const modificationTypeOf = (mod) =>
	mod.modificationType || mod.modification_type;

export const getOrderDisplayStatus = (order) => {
	if (order.deletedAt) return 'deleted';
	if (order.status === 'cancelled') return 'cancelled';

	const billedAt   = order.billedAt || order.billed_at;
	const billedMs   = billedAt ? new Date(billedAt).getTime() : null;
	const modHistory = order.modificationHistory || [];
	// All item-modification entries (not re_confirmed / served records)
	const modEntries = modHistory.filter(
		(mod) => modificationTypeOf(mod) === 'order_modification'
	);

	if (modEntries.length > 0) {
		if (billedMs) {
			// Simple flag check: backend sets modified_after_billed = true when a
			// modification happens while billed_at is already stamped on the order.
			// Once true it is never cleared, so a single field check is sufficient.
			const isPostBilled = order.modifiedAfterBilled || order.modified_after_billed || false;
			return isPostBilled ? 'post_print_modified' : 'pre_billed_modified';
		}
		// Has modifications but the order was never billed → orange
		return 'modified';
	}

	// ── Fallback for offline orders where modificationHistory may be absent ──
	// If currentModificationCycle > 0 the order was definitely modified at some point.
	const modCycle = order.currentModificationCycle ?? order.current_modification_cycle ?? 0;
	if (modCycle > 0) {
		// Without history entries we cannot confirm the modification happened before
		// billing — defaulting to pre_billed_modified would silently hide a potential
		// post-bill change. Safer to show orange (post_print_modified) when billedAt
		// is set, so staff are alerted to verify the bill.
		return billedMs ? 'post_print_modified' : 'modified';
	}

	// ── Re-confirmed (served/billed → confirmed → completed) ─────────────────
	if (order.status === 'completed') {
		const hasReconfirmed = modHistory.some(
			(mod) => modificationTypeOf(mod) === 're_confirmed'
		);
		if (hasReconfirmed) return 'reconfirmed_completed';
	}

	if (order.status === 'billed') return 'billed';
	if (order.status === 'completed') return 'completed';
	if (order.status === 'served') return 'served';
	if (order.status === 'ready') return 'ready';
	if (order.status === 'confirmed' && billedAt) return 'billed_to_confirmed';
	if (order.status === 'confirmed') return 'confirmed';
	if (order.status === 'preparing') return 'preparing';
	if (order.status === 'ordered') return 'ordered';

	return order.status;
};

// Filtered history orders selector
export const selectFilteredHistoryOrders = createSelector(
	[selectHistoryOrders, selectHistoryFilters],
	(orders, filters) => {
		let filtered = orders;
		
		// Apply status filter
		if (filters.statusFilter !== 'all') {
			filtered = filtered.filter(order => {
				const orderStatus = getOrderDisplayStatus(order);
			
				if (filters.statusFilter === 'modified') {
					return (
						orderStatus === 'modified' || orderStatus === 'post_print_modified'
					);
				} else if (filters.statusFilter === 'completed') {
					// 'completed' filter includes plain-completed AND re-confirmed-then-completed.
					// Without this, light-blue (reconfirmed_completed) cards vanish from
					// the history tab when the user selects the "completed" filter.
					return orderStatus === 'completed' || orderStatus === 'reconfirmed_completed';
				} else {
					return orderStatus === filters.statusFilter;
				}
			});
		}
		
		// Apply search term filter (client-side for better UX)
		if (filters.searchTerm && filters.searchTerm.trim()) {
			const searchLower = filters.searchTerm.toLowerCase().trim();
			filtered = filtered.filter(order => {
				// Search in customer name
				if (order.customerName && order.customerName.toLowerCase().includes(searchLower)) {
					return true;
				}
				// Search in table number
				if (order.tableNumber && order.tableNumber.toString().includes(searchLower)) {
					return true;
				}
				// Search in order items
				if (order.items && order.items.some(item => 
					item.name && item.name.toLowerCase().includes(searchLower)
				)) {
					return true;
				}
				// Search in order ID
				if (order._id && order._id.toLowerCase().includes(searchLower)) {
					return true;
				}
				return false;
			});
		}
		
		return filtered;
	}
);


